import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './Me.module.css';
import moranPhoto from '../../images/מורן וינברגר תדמית 1.webp';

gsap.registerPlugin(ScrollTrigger);

const AboutMe = () => {
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const titleRef = useRef(null);
  const quoteRef = useRef(null);
  const paragraphRefs = useRef([]);
  const dividerRefs = useRef([]);
  const ctaRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 75%',
          once: true,
        },
      });

      // Image - clip-path reveal from left side
      tl.fromTo(imageRef.current,
        { clipPath: 'inset(0% 100% 0% 0%)', scale: 1.1 },
        { clipPath: 'inset(0% 0% 0% 0%)', scale: 1, duration: 1.5, ease: 'power3.inOut' }
      );

      // Title - slide from right
      tl.fromTo(titleRef.current,
        { opacity: 0, x: 40 },
        { opacity: 1, x: 0, duration: 0.9, ease: 'power3.out' },
        '-=0.9'
      );

      // Quote - fade up
      tl.fromTo(quoteRef.current,
        { opacity: 0, y: 25 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' },
        '-=0.5'
      );

      // Paragraphs and dividers staggered
      paragraphRefs.current.forEach((p, i) => {
        tl.fromTo(p,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' },
          '-=0.4'
        );

        if (dividerRefs.current[i]) {
          tl.fromTo(dividerRefs.current[i],
            { scaleX: 0, opacity: 0 },
            { scaleX: 1, opacity: 1, duration: 0.7, ease: 'power2.inOut' },
            '-=0.4'
          );
        }
      });

      // CTA button
      tl.fromTo(ctaRef.current,
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' },
        '-=0.3'
      );

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className={styles.section} id="מי-אני">
      <div className={styles.container}>
        <div ref={imageRef} className={styles.imageWrapper}>
          <img src={moranPhoto} alt="מורן וינברגר, מעצבת פנים ומנהלת פרויקטים" className={styles.image} loading="lazy" decoding="async" />
        </div>

        <div className={styles.content}>
          <h2 ref={titleRef} className={styles.mainTitle} style={{ opacity: 0 }}>נעים להכיר, מורן וינברגר</h2>

          <div ref={quoteRef} className={styles.quoteContainer} style={{ opacity: 0 }}>
            <p className={styles.quote}>
              "כל חלל מספר סיפור. התפקיד שלי הוא להקשיב לסיפור שלכם ולתרגם אותו למקום שבאמת מרגיש כמו בית."
            </p>
          </div>

          <div className={styles.textBlock}>
            <p ref={el => paragraphRefs.current[0] = el} className={styles.paragraph} style={{ opacity: 0 }}>
              כל החיים שלי סבבו סביב יצירה וסדר.
              עוד לפני שנכנסתי לעולם העיצוב, עסקתי בניהול פרויקטים ותקציבים - ותמיד מצאתי את עצמי מסתכלת על כל בית שגרתי בו ושואלת: איך אפשר לגרום למקום הזה לעבוד טוב יותר, להרגיש נכון יותר.
              ברגע מסוים הבנתי שזו לא רק הרגל - זו שליחות. ואז התחלתי ללמוד, להתמקצע, ולהפוך את האינסטינקט הזה לקריירה.
            </p>

            <div ref={el => dividerRefs.current[0] = el} className={styles.divider} style={{ opacity: 0 }} />

            <p ref={el => paragraphRefs.current[1] = el} className={styles.paragraph} style={{ opacity: 0 }}>
              אני יודעת שתהליך של עיצוב או שיפוץ יכול להרגיש מורכב, ולפעמים אפילו מבלבל.
              לא תמיד יודעים מאיפה להתחיל, מה באמת רוצים, וכמה הכל הולך לעלות.
              בדיוק בשביל זה אני כאן - להוריד את העומס, להכניס סדר, ולתת לכם את הביטחון שמישהי מובילה את הדרך.
              הגישה שלי מבוססת על הקשבה אמיתית, שקיפות מלאה, ותכנון שלא משאיר שאלות פתוחות.
            </p>

            <div ref={el => dividerRefs.current[1] = el} className={styles.divider} style={{ opacity: 0 }} />

            <p ref={el => paragraphRefs.current[2] = el} className={styles.paragraph} style={{ opacity: 0 }}>
              אני מאמינה שעיצוב טוב הוא לא רק מה שיפה לעין - אלא מה שעובד בחיים.
              מקום שמותאם לכם, לשגרה שלכם, לאופי שלכם.
              אחרי מעל עשור בתחום, הדבר שהכי מניע אותי הוא הרגע שבו לקוחות נכנסים למקום ומרגישים שמישהי באמת הקשיבה להם.
              זו ההנאה הכי גדולה שלי - ובשביל זה אני קמה כל בוקר.
            </p>
          </div>

          <button ref={ctaRef} className={styles.ctaButton} style={{ opacity: 0 }}>
            לחצו כאן ונדבר על הפרויקט שלכם
          </button>
        </div>
      </div>
    </section>
  );
};

export default AboutMe;
